<template>
  <h6 class="mt-10">
    {{ option.title }}
  </h6>
  <div class="mb-2">
    <div class="limit-wrap">
      <div>Max: {{ option.max }} / Min: {{ option.min }}</div>

      <div v-if="option.maxAggregateOptionsQuantity || option.minAggregateOptionsQuantity">
        (Aggregate Amount) Max: {{ option.maxAggregateOptionsQuantity }} / Min: {{ option.minAggregateOptionsQuantity || 0 }}
      </div>
    </div>
    <div
      v-if="showErrors && hasLimit"
      class="input-error"
    >
      Error: Please fix below options
    </div>
  </div>

  <div
    v-for="(value, valueIndex) in option.values"
    :key="valueIndex"
  >
    <div
      class="store-item-value"
      @click="onExpand(value)"
    >
      <div v-if="option.type === 'input'">
        <el-input-number
          v-model="option.selectedValues[value.id]"
          size="small"
          class="input-amount"
          :min="0"
          :max="getQuantityMax(value.id)"
          :class="{ invalid: showErrors && hasLimit }"
          :disabled="disabledValues[option.id] && disabledValues[option.id].includes(value.id)"
          @change="numberOptionChanged(value, $event)"
        />
        {{ value.title }}
        <span v-if="value.max || value.min"> (Max: {{ value.max }} / Min: {{ value.min }}) </span>
        <span v-if="value.price"> + ${{ (value.price / 100).toFixed(2) }} </span>
        <span v-if="value.default"> (default: {{ value.default }}) </span>
      </div>
      <div v-else>
        <el-checkbox
          v-model="option.selectedValues[value.id]"
          :disabled="countSelected >= option.max && option.selectedValues[value.id] !== true"
          @change="checkboxOptionChanged(value, $event)"
        >
          {{ value.title }}<span v-if="value.price"> + ${{ (value.price / 100).toFixed(2) }}</span>
        </el-checkbox>
      </div>
      <i
        v-if="value.nextCursor || value.subOptions?.length > 0"
        :class="expandedValueId === value.id ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"
      />
    </div>
    <div
      v-show="option.selectedValues[value.id] && expandedValueId === value.id"
      class="store-item-options"
    >
      <fieldset
        v-for="(subOption, subOptionIndex) in value.subOptions"
        :key="subOptionIndex"
      >
        <store-item-option
          :item="item"
          :is-parent-selected="option.selectedValues[value.id]"
          :option="subOption"
          :show-errors="showErrors"
          @loading="onLoading"
        />
      </fieldset>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, Ref, toRefs, computed } from 'vue';
import { isEmpty } from 'lodash';
import { gourmetService } from '@/services/gourmet';

export default {
  props: {
    item: {
      type: Object,
      default: () => {},
    },
    option: {
      type: Object,
      default: () => {},
    },
    showErrors: {
      type: Boolean,
      default: () => false,
    },
  },
  emits: ['loading'],

  setup: (props: any, context: any) => {
    const { emit } = context;
    const { option, item } = toRefs(props);
    const expandedValueId = ref('');
    const fetchedValueIds: Ref<string[]> = ref([]);

    const disabledValues: Ref<any> = ref({});

    const countSelected = computed(() => {
      let count = 0;

      for (const key in option.value.selectedValues) {
        const value = option.value.selectedValues[key];

        if (typeof value === 'boolean' && value) {
          count++;
        } else {
          count += value;
        }
      }

      return count;
    });

    const countSelectedOptions = computed(() => {
      let count = 0;

      for (const key in option.value.selectedValues) {
        if (option.value.selectedValues[key]) {
          count++;
        }
      }

      return count;
    });

    const hasLimit = computed(() => {
      const selectedAmount = countSelected.value;
      const selectedOptionCount = countSelectedOptions.value;

      if (option.value.max && selectedOptionCount > option.value.max) {
        return true;
      }

      if (option.value.min && selectedOptionCount < option.value.min) {
        return true;
      }

      if (option.value.maxAggregateOptionsQuantity && selectedAmount > option.value.maxAggregateOptionsQuantity) {
        return true;
      }

      if (option.value.minAggregateOptionsQuantity && selectedAmount < option.value.minAggregateOptionsQuantity) {
        return true;
      }

      return false;
    });

    const calculateOptionsLimit = (valueId: any) => {
      const selectedAmount = countSelected.value;
      const selectedOptionCount = countSelectedOptions.value;
      const isValuesReached = selectedOptionCount >= option.value.max;
      const isAmountReached = selectedAmount >= option.value.maxAggregateOptionsQuantity;

      disabledValues.value[option.value.id] = [];
      for (const v of option.value.values) {
        if (!option.value.selectedValues[v.id] && (isAmountReached || isValuesReached)) {
          disabledValues.value[option.value.id].push(v.id);
        }
      }

      let amountExcludedCurrent = selectedAmount - option.value.selectedValues[valueId];
      option.value.selectedValues[valueId] =
        selectedAmount > option.value.maxAggregateOptionsQuantity
          ? option.value.maxAggregateOptionsQuantity - amountExcludedCurrent
          : option.value.selectedValues[valueId];
    };

    const getQuantityMax = (valueId: any) => {
      let selectedAmount = 0;

      for (const key in option.value.selectedValues) {
        if (key === valueId) {
          continue;
        }

        const value = option.value.selectedValues[key];

        if (typeof value === 'boolean' && value) {
          selectedAmount++;
        } else {
          selectedAmount += value;
        }
      }

      return option.value.maxAggregateOptionsQuantity - selectedAmount;
    };

    const numberOptionChanged = (value: any, event: any) => {
      if (option.value.selectedValues[value.id] === '') {
        option.value.selectedValues[value.id] = event;
      }

      calculateOptionsLimit(value.id);

      if (event) {
        expandedValueId.value = value.id;
      } else {
        expandedValueId.value = '';
      }

      if (event && value.nextCursor && !value.subOptions?.length && !fetchedValueIds.value.includes(value.id)) {
        fetchSubOptions(value);
      }
    };

    const checkboxOptionChanged = (value: any, event: boolean) => {
      if (event) {
        expandedValueId.value = value.id;
      } else {
        expandedValueId.value = '';
      }

      if (event && value.nextCursor && !value.subOptions?.length && !fetchedValueIds.value.includes(value.id)) {
        fetchSubOptions(value);
      }
    };

    const onExpand = (value: any) => {
      if (expandedValueId.value === value.id) {
        expandedValueId.value = '';

        return;
      }

      if (!fetchedValueIds.value.includes(value.id)) {
        fetchSubOptions(value);
      }
      expandedValueId.value = value.id;
    };

    const assignSelectedValues = (options: any[], originalOptions: any[]) => {
      for (const option of options) {
        const originalOption = originalOptions.find((entry) => entry.id === option.id);

        if (!originalOption) {
          continue;
        }

        if (!isEmpty(originalOption.selectedValues)) {
          option.selectedValues = originalOption.selectedValues;
        }

        if (!originalOption.values?.length) {
          continue;
        }

        if (option.values?.length) {
          option.values = originalOption.values;
        } else {
          for (const value of option.values) {
            const subOriginalValue = originalOption.values.find((entry: any) => entry.id === value.id);

            if (!subOriginalValue?.subOptions.length) {
              continue;
            }

            if (!value.subOptions?.length) {
              value.subOptions = subOriginalValue.subOptions;
            } else {
              value.subOptions = assignSelectedValues(options, value.subOptions);
            }
          }
        }
      }

      return options;
    };

    const setValueOptions = (value: any, options: any[]) => {
      if (!value.subOptions) {
        value.subOptions = options;
      } else {
        value.subOptions = assignSelectedValues(options, value.subOptions);
      }
    };

    const fetchSubOptions = async (value: any) => {
      if (!value.nextCursor) {
        return;
      }

      try {
        emit('loading', true);
        const res = await gourmetService.getItemDetails({
          item: item.value,
          optionsCursor: value.nextCursor,
        });

        setValueOptions(value, res.options ?? []);
        fetchedValueIds.value.push(value.id);
      } catch (err) {
        value.subOptions = null;
      } finally {
        emit('loading', false);
      }
    };

    const onLoading = (value: boolean) => {
      emit('loading', value);
    };

    return {
      disabledValues,
      countSelected,
      hasLimit,
      expandedValueId,

      calculateOptionsLimit,
      getQuantityMax,
      onLoading,
      numberOptionChanged,
      checkboxOptionChanged,
      onExpand,
    };
  },
};
</script>

<style lang="scss">
.store-item-value {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}
</style>
